/**
 * Service Sales - Sales Operations
 *
 * Core CRUD operations for service sales and budgets.
 * Integrates with Supabase for data persistence and provides
 * type-safe operations with comprehensive error handling.
 *
 * @module services/sales/salesService
 */

import { supabase } from '@/integrations/supabase/client';
import type {
  Database,
  TablesInsert,
  TablesUpdate,
  Enums
} from '@/integrations/supabase/types';
import type { ServiceSaleSummary as UIServiceSaleSummary } from '@/types/serviceSales';

// Type definitions for service sales
export type ServiceSale = Database['public']['Tables']['service_sales']['Row'];
export type ServiceSaleInsert = TablesInsert<'service_sales'>;
export type ServiceSaleUpdate = TablesUpdate<'service_sales'>;

export type ServiceSaleStatus = Database['public']['Enums']['budget_status'];
export type ServiceType = Database['public']['Enums']['service_type'];
export type DiscountType = Database['public']['Enums']['budget_discount_type'];

export interface ServiceSaleFilters {
  status?: ServiceSaleStatus | ServiceSaleStatus[];
  type?: ServiceType | ServiceType[];
  clientId?: string;
  companyId?: number;
  userId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  limit?: number;
  offset?: number;
  orderBy?: 'created_at' | 'sale_date' | 'updated_at' | 'total_value';
  orderDirection?: 'asc' | 'desc';
}

// Internal API summary type (deprecated - use UIServiceSaleSummary instead)
export interface ServiceSaleSummary {
  totalSales: number;
  totalBudgets: number;
  totalValue: number;
  averageValue: number;
  conversionRate: number;
  approvedCount: number;
  pendingCount: number;
  draftCount: number;
  expiredCount: number;
  rejectedCount: number;
  convertedCount: number;
  // Value breakdown by status
  approvedValue: number;
  pendingValue: number;
  draftValue: number;
  expiredValue: number;
  rejectedValue: number;
  convertedValue: number;
}

export interface ServiceSaleWithItems extends ServiceSale {
  items: ServiceSaleItem[];
}

export interface ServiceSaleItem {
  id: string;
  service_sale_id: string;
  description: string;
  quantity: number;
  unit_price: number;
  total_amount: number | null;
  discount_type?: DiscountType | null;
  discount_value?: number | null;
  tax_percentage?: number | null;
  notes?: string | null;
  category_id?: number | null;
  unit?: string | null;
  sort_order?: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface CreateServiceSaleInput {
  type: ServiceType;
  clientId?: string | null;
  companyId: number;
  userId: string;
  saleDate?: string;
  issueDate?: string;
  expiryDate?: string;
  notes?: string;
  internalNotes?: string;
  termsConditions?: string;
  clientNotes?: string;
  discountType?: DiscountType;
  discountValue?: number;
  costCenterId?: number | null;
  salespersonId?: string | null;
  items: Omit<ServiceSaleItem, 'id' | 'service_sale_id' | 'created_at' | 'updated_at'>[];
}

export interface UpdateServiceSaleInput {
  status?: ServiceSaleStatus;
  clientId?: string | null;
  saleDate?: string;
  issueDate?: string;
  expiryDate?: string;
  notes?: string;
  internalNotes?: string;
  termsConditions?: string;
  clientNotes?: string;
  discountType?: DiscountType;
  discountValue?: number;
  costCenterId?: number | null;
  salespersonId?: string | null;
  approvedBy?: string;
  rejectedBy?: string;
  rejectionReason?: string;
  convertedToTransactionId?: string | null;
}

/**
 * Fetch service sales with optional filtering and pagination
 */
export async function fetchServiceSales(
  filters: ServiceSaleFilters = {}
): Promise<ServiceSale[]> {
  let query = supabase
    .from('service_sales')
    .select(`
      *,
      client:clients(
        id,
        name,
        document_number,
        email,
        phone
      ),
      company:companies(
        id,
        name
      )
    `);

  // Apply filters
  if (filters.status) {
    if (Array.isArray(filters.status)) {
      query = query.in('status', filters.status);
    } else {
      query = query.eq('status', filters.status);
    }
  }

  if (filters.type) {
    if (Array.isArray(filters.type)) {
      query = query.in('type', filters.type);
    } else {
      query = query.eq('type', filters.type);
    }
  }

  if (filters.clientId) {
    query = query.eq('client_id', filters.clientId);
  }

  if (filters.companyId) {
    query = query.eq('company_id', filters.companyId);
  }

  if (filters.userId) {
    query = query.eq('user_id', filters.userId);
  }

  if (filters.dateFrom) {
    // Ensure date is in YYYY-MM-DD format without timezone
    let dateStr = filters.dateFrom;
    if (filters.dateFrom instanceof Date) {
      const year = filters.dateFrom.getFullYear();
      const month = String(filters.dateFrom.getMonth() + 1).padStart(2, '0');
      const day = String(filters.dateFrom.getDate()).padStart(2, '0');
      dateStr = `${year}-${month}-${day}`;
    }
    query = query.gte('sale_date', dateStr);
  }

  if (filters.dateTo) {
    // Ensure date is in YYYY-MM-DD format without timezone
    let dateStr = filters.dateTo;
    if (filters.dateTo instanceof Date) {
      const year = filters.dateTo.getFullYear();
      const month = String(filters.dateTo.getMonth() + 1).padStart(2, '0');
      const day = String(filters.dateTo.getDate()).padStart(2, '0');
      dateStr = `${year}-${month}-${day}`;
    }
    query = query.lte('sale_date', dateStr);
  }

  if (filters.search) {
    query = query.or(`
      number.ilike.%${filters.search}%,
      notes.ilike.%${filters.search}%,
      client.name.ilike.%${filters.search}%
    `);
  }

  // Apply ordering
  const orderBy = filters.orderBy || 'created_at';
  const orderDirection = filters.orderDirection || 'desc';
  query = query.order(orderBy, { ascending: orderDirection === 'asc' });

  // Apply pagination
  if (filters.limit) {
    query = query.limit(filters.limit);
  }

  if (filters.offset) {
    query = query.range(filters.offset, filters.offset + (filters.limit || 50) - 1);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching service sales:', error);
    throw new Error(`Failed to fetch service sales: ${error.message}`);
  }

  return data || [];
}

/**
 * Fetch a single service sale by ID with related data
 */
export async function fetchServiceSaleById(id: string): Promise<ServiceSaleWithItems | null> {
  const { data: sale, error: saleError } = await supabase
    .from('service_sales')
    .select(`
      *,
      client:clients(
        id,
        name,
        document_number,
        email,
        phone,
        address_street,
        address_number,
        address_complement,
        address_neighborhood,
        address_city,
        address_state,
        address_zip_code
      ),
      company:companies(
        id,
        name
      )
    `)
    .eq('id', id)
    .single();

  if (saleError) {
    if (saleError.code === 'PGRST116') {
      return null; // Not found
    }
    console.error('Error fetching service sale:', saleError);
    throw new Error(`Failed to fetch service sale: ${saleError.message}`);
  }

  // Fetch items for this sale
  const { data: items, error: itemsError } = await supabase
    .from('service_sale_items')
    .select('*')
    .eq('service_sale_id', id)
    .order('sort_order', { ascending: true });

  if (itemsError) {
    console.error('Error fetching service sale items:', itemsError);
    throw new Error(`Failed to fetch service sale items: ${itemsError.message}`);
  }

  return {
    ...sale,
    items: items || []
  };
}

/**
 * Create a new service sale with items in a transaction
 */
export async function createServiceSaleWithItems(
  input: CreateServiceSaleInput
): Promise<ServiceSaleWithItems> {
  try {
    // Calculate totals
    const subtotal = input.items.reduce((sum, item) =>
      sum + (item.quantity * item.unit_price), 0
    );

    const taxAmount = input.items.reduce((sum, item) =>
      sum + ((item.quantity * item.unit_price) * (item.tax_percentage || 0) / 100), 0
    );

    // Apply discount
    let discountAmount = 0;
    if (input.discountType && input.discountValue) {
      discountAmount = input.discountType === 'percentage'
        ? (subtotal * input.discountValue) / 100
        : input.discountValue;
    }

    const totalValue = subtotal + taxAmount - discountAmount;

    // Generate sale number
    const saleNumber = await generateSaleNumber(input.type, input.companyId);

    // Create the main sale record
    const saleData: ServiceSaleInsert = {
      type: input.type,
      number: saleNumber,
      client_id: input.clientId,
      company_id: input.companyId,
      user_id: input.userId,
      sale_date: input.saleDate || new Date().toISOString().split('T')[0],
      issue_date: input.issueDate || new Date().toISOString().split('T')[0],
      expiry_date: input.expiryDate || null,
      status: 'draft',
      notes: input.notes || null,
      internal_notes: input.internalNotes || null,
      terms_conditions: input.termsConditions || null,
      client_notes: input.clientNotes || null,
      discount_type: input.discountType || null,
      discount_value: input.discountValue || null,
      cost_center_id: input.costCenterId || null,
      salesperson_id: input.salespersonId || null,
      subtotal,
      tax_amount: taxAmount,
      total_value: totalValue,
    };

    const { data: sale, error: saleError } = await supabase
      .from('service_sales')
      .insert([saleData])
      .select()
      .single();

    if (saleError) {
      console.error('Error creating service sale:', saleError);
      throw new Error(`Failed to create service sale: ${saleError.message}`);
    }

    // Create items
    const itemsToInsert = input.items.map((item, index) => ({
      service_sale_id: sale.id,
      description: item.description,
      quantity: item.quantity,
      unit_price: item.unit_price,
      // total_amount is a GENERATED column - calculated by database
      discount_type: item.discount_type,
      discount_value: item.discount_value,
      tax_percentage: item.tax_percentage,
      notes: item.notes,
      category_id: item.category_id,
      unit: item.unit,
      sort_order: item.sort_order || index,
    }));

    const { data: items, error: itemsError } = await supabase
      .from('service_sale_items')
      .insert(itemsToInsert)
      .select('*')
      .order('sort_order', { ascending: true });

    if (itemsError) {
      // Rollback sale creation
      await supabase.from('service_sales').delete().eq('id', sale.id);
      console.error('Error creating service sale items:', itemsError);
      throw new Error(`Failed to create service sale items: ${itemsError.message}`);
    }

    return {
      ...sale,
      items: items || []
    };

  } catch (error) {
    console.error('Error in createServiceSaleWithItems:', error);
    throw error;
  }
}

/**
 * Update an existing service sale
 */
export async function updateServiceSale(
  id: string,
  updates: UpdateServiceSaleInput
): Promise<ServiceSale> {
  const updateData: ServiceSaleUpdate = {};

  // Map updates to database fields
  if (updates.status !== undefined) updateData.status = updates.status;
  if (updates.clientId !== undefined) updateData.client_id = updates.clientId;
  if (updates.saleDate !== undefined) updateData.sale_date = updates.saleDate;
  if (updates.issueDate !== undefined) updateData.issue_date = updates.issueDate;
  if (updates.expiryDate !== undefined) updateData.expiry_date = updates.expiryDate;
  if (updates.notes !== undefined) updateData.notes = updates.notes;
  if (updates.internalNotes !== undefined) updateData.internal_notes = updates.internalNotes;
  if (updates.termsConditions !== undefined) updateData.terms_conditions = updates.termsConditions;
  if (updates.clientNotes !== undefined) updateData.client_notes = updates.clientNotes;
  if (updates.discountType !== undefined) updateData.discount_type = updates.discountType;
  if (updates.discountValue !== undefined) updateData.discount_value = updates.discountValue;
  if (updates.costCenterId !== undefined) updateData.cost_center_id = updates.costCenterId;
  if (updates.salespersonId !== undefined) updateData.salesperson_id = updates.salespersonId;
  if (updates.approvedBy !== undefined) updateData.approved_by = updates.approvedBy;
  if (updates.rejectedBy !== undefined) updateData.rejected_by = updates.rejectedBy;
  if (updates.rejectionReason !== undefined) updateData.rejection_reason = updates.rejectionReason;
  if (updates.convertedToTransactionId !== undefined) {
    updateData.converted_to_transaction_id = updates.convertedToTransactionId;
  }

  // Add timestamps for status changes
  if (updates.status === 'approved') {
    updateData.approved_date = new Date().toISOString();
  } else if (updates.status === 'rejected') {
    updateData.rejected_date = new Date().toISOString();
  } else if (updates.status === 'converted') {
    updateData.converted_date = new Date().toISOString();
  } else if (updates.status === 'sent') {
    updateData.sent_date = new Date().toISOString();
  }

  updateData.updated_at = new Date().toISOString();

  const { data, error } = await supabase
    .from('service_sales')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating service sale:', error);
    throw new Error(`Failed to update service sale: ${error.message}`);
  }

  return data;
}

/**
 * Delete a service sale (cascade deletes items)
 */
export async function deleteServiceSale(id: string): Promise<void> {
  const { error } = await supabase
    .from('service_sales')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting service sale:', error);
    throw new Error(`Failed to delete service sale: ${error.message}`);
  }
}

/**
 * Generate service sales summary statistics
 */
export async function fetchServiceSalesSummary(
  filters: ServiceSaleFilters = {}
): Promise<UIServiceSaleSummary> {
  let query = supabase
    .from('service_sales')
    .select('status, type, total_value');

  // Apply same filters as main query
  if (filters.status) {
    if (Array.isArray(filters.status)) {
      query = query.in('status', filters.status);
    } else {
      query = query.eq('status', filters.status);
    }
  }

  if (filters.type) {
    if (Array.isArray(filters.type)) {
      query = query.in('type', filters.type);
    } else {
      query = query.eq('type', filters.type);
    }
  }

  if (filters.clientId) {
    query = query.eq('client_id', filters.clientId);
  }

  if (filters.companyId) {
    query = query.eq('company_id', filters.companyId);
  }

  if (filters.userId) {
    query = query.eq('user_id', filters.userId);
  }

  if (filters.dateFrom) {
    // Ensure date is in YYYY-MM-DD format without timezone
    let dateStr = filters.dateFrom;
    if (filters.dateFrom instanceof Date) {
      const year = filters.dateFrom.getFullYear();
      const month = String(filters.dateFrom.getMonth() + 1).padStart(2, '0');
      const day = String(filters.dateFrom.getDate()).padStart(2, '0');
      dateStr = `${year}-${month}-${day}`;
    }
    query = query.gte('sale_date', dateStr);
  }

  if (filters.dateTo) {
    // Ensure date is in YYYY-MM-DD format without timezone
    let dateStr = filters.dateTo;
    if (filters.dateTo instanceof Date) {
      const year = filters.dateTo.getFullYear();
      const month = String(filters.dateTo.getMonth() + 1).padStart(2, '0');
      const day = String(filters.dateTo.getDate()).padStart(2, '0');
      dateStr = `${year}-${month}-${day}`;
    }
    query = query.lte('sale_date', dateStr);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching service sales summary:', error);
    throw new Error(`Failed to fetch service sales summary: ${error.message}`);
  }

  const sales = data || [];

  const totalSales = sales.filter(s => s.type === 'sale').length;
  const totalBudgets = sales.filter(s => s.type === 'budget').length;
  const totalValue = sales.reduce((sum, s) => sum + (s.total_value || 0), 0);
  const averageValue = sales.length > 0 ? totalValue / sales.length : 0;

  // Calculate values for each status
  const canceledValue = sales
    .filter(s => s.status === 'cancelled' || s.status === 'rejected')
    .reduce((sum, s) => sum + (s.total_value || 0), 0);

  const canceledCount = sales.filter(s => s.status === 'cancelled' || s.status === 'rejected').length;

  const forecastedValue = sales
    .filter(s => s.status === 'sent' || s.status === 'draft')
    .reduce((sum, s) => sum + (s.total_value || 0), 0);

  const forecastedCount = sales.filter(s => s.status === 'sent' || s.status === 'draft').length;

  const approvedValue = sales
    .filter(s => s.status === 'approved' || s.status === 'converted')
    .reduce((sum, s) => sum + (s.total_value || 0), 0);

  const approvedCount = sales.filter(s => s.status === 'approved' || s.status === 'converted').length;

  return {
    canceled: {
      count: canceledCount,
      value: canceledValue,
    },
    forecasted: {
      count: forecastedCount,
      value: forecastedValue,
    },
    approved: {
      count: approvedCount,
      value: approvedValue,
    },
    total: {
      count: sales.length,
      value: totalValue,
    },
  };
}

/**
 * Generate a unique sale number based on type and company
 */
async function generateSaleNumber(type: ServiceType, companyId: number): Promise<string> {
  const prefix = type === 'budget' ? 'ORC' : 'VND';
  const year = new Date().getFullYear();

  // Get count for this type, company, and year
  const { data, error } = await supabase
    .from('service_sales')
    .select('number')
    .eq('type', type)
    .eq('company_id', companyId)
    .like('number', `${prefix}${year}%`)
    .order('number', { ascending: false })
    .limit(1);

  if (error) {
    console.error('Error generating sale number:', error);
    // Fallback to timestamp-based number
    return `${prefix}${year}${Date.now().toString().slice(-4)}`;
  }

  let sequence = 1;
  if (data && data.length > 0) {
    const lastNumber = data[0].number;
    const lastSequence = parseInt(lastNumber.replace(`${prefix}${year}`, ''), 10);
    sequence = (lastSequence || 0) + 1;
  }

  return `${prefix}${year}${sequence.toString().padStart(4, '0')}`;
}

/**
 * Batch operations for service sales
 */
export async function batchUpdateStatus(
  saleIds: string[],
  status: ServiceSaleStatus,
  userId: string
): Promise<{ success: number; failed: number; errors: string[] }> {
  const results = { success: 0, failed: 0, errors: [] as string[] };

  for (const id of saleIds) {
    try {
      await updateServiceSale(id, {
        status,
        approvedBy: status === 'approved' ? userId : undefined,
        rejectedBy: status === 'rejected' ? userId : undefined,
      });
      results.success++;
    } catch (error) {
      results.failed++;
      results.errors.push(`Failed to update sale ${id}: ${error}`);
    }
  }

  return results;
}

/**
 * Search service sales by text across multiple fields
 */
export async function searchServiceSales(
  searchTerm: string,
  limit: number = 20
): Promise<ServiceSale[]> {
  const { data, error } = await supabase
    .from('service_sales')
    .select(`
      *,
      client:clients(
        id,
        name,
        document_number
      )
    `)
    .or(`
      number.ilike.%${searchTerm}%,
      notes.ilike.%${searchTerm}%,
      client.name.ilike.%${searchTerm}%,
      client.document_number.ilike.%${searchTerm}%
    `)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error searching service sales:', error);
    throw new Error(`Failed to search service sales: ${error.message}`);
  }

  return data || [];
}